/**
 * Derived values for the recurring-schedules page (spec 075). A plain module, not
 * a component file, so the component files stay clean under
 * `react-refresh/only-export-components` — same split as `queueFormat.js`.
 */
import { jobTrigger } from './jobIdentity'
import { fmtDuration, fmtRowCount } from './queueFormat'

/**
 * The word each run state reads as. Run history's Outcome column and the schedules
 * table's Last-run cell both print it, so one run is never named two ways on one page.
 */
export const RUN_STATE_LABEL = Object.freeze({
  ready: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  running: 'Running',
  queued: 'Queued',
  scheduled: 'Scheduled',
})

/** A D1 epoch-ms value as a number, or null — never NaN, never a string compare. */
const epochMs = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * The newest run in a schedule's run list — the run the Last-run cell describes
 * (CC-53474, spec 182).
 *
 * Chosen by comparing `created_at` as NUMBERS, then `id`, which is exactly cb-api's
 * `ORDER BY created_at DESC, id DESC` (`schedulesStore.js`), so it is the same row as
 * the server's `lastJobId` (`runs[0]`). It does not trust the array's order: a
 * newest-first list and an unordered one give the same answer, and a text compare
 * (`"9…" > "1…"`, the D1 `MAX()` trap) can never decide it.
 *
 * Null when there is no run with a readable `created_at` — a colleague's row (the API
 * withholds its runs) or a schedule with nothing listed.
 */
export function newestRun(runs) {
  let best = null
  let bestAt = null
  for (const r of Array.isArray(runs) ? runs : []) {
    const at = epochMs(r?.created_at)
    if (at == null) continue
    if (best == null || at > bestAt || (at === bestAt && String(r.id) > String(best.id))) {
      best = r
      bestAt = at
    }
  }
  return best
}

/**
 * Everything the schedules table's "Last run" cell prints, taken from ONE run.
 *
 * ## The defect (CC-53474)
 *
 * The cell used to assemble three facts from three sources: the TIME from the stored
 * `lastRunAt`, which only the scheduler writes (Run now deliberately does not, so a
 * dormant schedule never looks healthy); the LINK from `lastJobId`, the newest run of
 * any kind (CC-53438); and the "rows · duration" FACTS from the newest *ready* run. So
 * a Run-now-only schedule read "Hasn't run yet" beside "1 of 1 ran", and a Run now
 * after a scheduled firing printed the scheduler's time over the Run-now run's link
 * and numbers.
 *
 * ## The rule now (Jordan's decision, 2026-10-15)
 *
 * "Last run" is the NEWEST run of any kind. Its time (`created_at`, the instant Run
 * history's "Ran" column prints), its link and its facts all come from `newestRun`,
 * and "Last N runs" (`RunHealthText`) reads the same `runs` array, so the two cells
 * can no longer describe different job sets.
 *
 * A Run-now run is LABELLED "Run now", so it is never mistaken for the scheduler
 * firing. The label comes from `jobTrigger`, the one provenance rule the Queue's row
 * glyph and drawer already use, so this cell and the drawer can never disagree about
 * one run. Its "From a schedule" answer (equality on a whole minute, or no
 * `scheduled_for` from an older worker) adds no label: it claims nothing.
 *
 * The state is stated in words when the run is not a success ("Failed", "Running" —
 * `RUN_STATE_LABEL`), so a failed newest run is no longer shown with an older
 * success's numbers under it.
 *
 * Returns null when the row carries no run; the cell then falls back to the stored
 * `lastRunAt`, which is the last SCHEDULED firing and is labelled as such where the
 * reader cannot see the runs.
 *
 * @returns {{ jobId: string, at: number, runNow: boolean, state: string, facts: string|null } | null}
 */
export function lastRunSummary(schedule) {
  const run = newestRun(schedule?.runs)
  if (!run) return null
  const at = epochMs(run.created_at)
  const trigger = jobTrigger({
    scheduleId: schedule?.id ?? run.schedule_id ?? null,
    scheduledFor: epochMs(run.scheduled_for),
    createdAt: at,
  })
  const runNow = trigger.key === 'run-now'
  const outcome = run.state === 'ready'
    // Spec 167: `fmtRowCount`, so a one-row run reads "1 row", not "1 rows".
    ? [run.row_count != null ? fmtRowCount(run.row_count) : null,
       run.duration_ms != null ? fmtDuration(run.duration_ms) : null].filter(Boolean).join(' · ')
    : (RUN_STATE_LABEL[run.state] ?? '')
  const facts = [runNow ? 'Run now' : null, outcome || null].filter(Boolean).join(' · ')
  return { jobId: run.id, at, runNow, state: run.state, facts: facts || null }
}

/** Terminal states that mean "this run did not produce a result". */
const FAILED = new Set(['failed', 'cancelled'])

/** A run the health strip should draw at all — queued/running has no outcome yet. */
const isFinished = (r) => r?.state === 'ready' || FAILED.has(r?.state)

export const isFailedRun = (r) => FAILED.has(r?.state)

export function summarizeRuns(runs = []) {
  const finished = runs.filter(isFinished)
  const failures = finished.filter(isFailedRun)
  return { finished, failures, total: finished.length, failureCount: failures.length }
}

/** "Jul 30" — the day a run happened, for the plain-language note. */
export const fmtRunDay = (ms) => {
  try { return new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' }) } catch { return 'an earlier run' }
}

/**
 * The failure note the design puts beside the sparkline ("1 failure on Jul 30").
 *
 * Plain language and a DATE, not a count alone: the question someone actually has
 * is "when did this last let me down", and a bare "1 failure" does not answer it.
 * Returns null when there is nothing to say — the row must not render an empty
 * reassurance.
 */
export function failureNote(runs = []) {
  const { failures } = summarizeRuns(runs)
  if (failures.length === 0) return null
  if (failures.length === 1) return `1 failure on ${fmtRunDay(failures[0].created_at)}`
  return `${failures.length} failures, latest ${fmtRunDay(failures[0].created_at)}`
}

/** Local midnight of the day `ms` falls in. */
export const startOfDay = (ms) => {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/*
 * --- The schedule's own zone -------------------------------------------------
 *
 * A PORT of cb-api's `recurrence.js` `tzOffsetMs` / `zonedParts` /
 * `zonedTimeToUtc` / `addDaysToParts` / `daysBetween` — the functions the cron
 * fires by — kept byte-for-byte in behaviour, including how a repeated fall-back
 * hour and a spring-forward gap resolve. `scheduleFormat.test.js` imports the
 * engine itself and fails if the two ever disagree. Only the formatter cache is
 * new: the calendar asks for the same few zones on every render.
 */
const DAY_MS = 86_400_000
const zoneFormatters = new Map()

/** Offset (ms) of `tz` from UTC at the instant `ts`. Throws RangeError on an unknown zone. */
function tzOffsetMs(ts, tz) {
  let dtf = zoneFormatters.get(tz)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
    zoneFormatters.set(tz, dtf)
  }
  const p = Object.fromEntries(dtf.formatToParts(new Date(ts)).map((x) => [x.type, x.value]))
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second)
  return asUTC - ts
}

/** The calendar/clock parts of `ts` in `tz`. */
function zonedParts(ts, tz) {
  const d = new Date(ts + tzOffsetMs(ts, tz))
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    weekday: d.getUTCDay(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  }
}

/** The instant of a wall time in `tz`, resolved twice exactly as the engine does. */
function zonedTimeToUtc({ year, month, day, hour, minute }, tz) {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const off1 = tzOffsetMs(guess, tz)
  let ts = guess - off1
  const off2 = tzOffsetMs(ts, tz)
  if (off2 !== off1) ts = guess - off2
  return ts
}

function addDaysToParts(parts, n) {
  const d = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + n))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), weekday: d.getUTCDay() }
}

function daysBetween(a, b) {
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY_MS)
}

/**
 * The zone the cron computes this schedule in: the rule's own `tz` (what
 * `runDueSchedules` passes to `nextRunAt`), else the row's `timezone` (written
 * alongside it, always equal), else UTC — the default both `normalizeRecurrence`
 * and `toApi` apply.
 */
const scheduleZone = (schedule) => {
  const tz = schedule.recurrence?.tz || schedule.timezone
  return typeof tz === 'string' && tz ? tz : 'UTC'
}

/**
 * The firings of one schedule inside [from, to), for the beta week calendar.
 *
 * Daily and weekly rules are safe to project here: the next run is known and the
 * step is a fixed number of days. Anything else contributes at most its single
 * known next run — this deliberately does NOT re-implement the monthly calendar
 * maths. `recurrence.js` on the server is the one engine for that (the recurrence
 * editor's own preview is server-computed for exactly this reason), and a second
 * implementation that disagrees about "the last Friday of the month" is the bug
 * nobody notices until month-end.
 *
 * ## Computed in the SCHEDULE's zone, bucketed in the viewer's
 *
 * Each later firing is the rule's wall time on a calendar day counted in the
 * schedule's own zone, converted to an instant by the engine's own conversion
 * (above) — the same computation the cron makes. `ScheduleCalendarPanel` then
 * drops each instant into the VIEWER's day column. Two earlier versions were
 * wrong in different ways (CC-53898):
 *
 * - origin/qa added a flat 86,400,000ms per day. The weekly loop visited a 25-hour
 *   fall-back day twice and drew its run twice; the daily loop carried elapsed
 *   time across the SCHEDULE's change, so a Chicago 7:00 AM read "6:00 AM" after it.
 * - the first fix (2026-09-28) stepped the VIEWER's calendar and re-applied the
 *   next run's VIEWER wall time. Correct only when the two zones are the same: a
 *   daily 7:00 AM UTC schedule seen from New York read "8:00 AM" from 2026-11-01
 *   on (UTC has no DST; New York does), and a weekly rule was matched against the
 *   viewer's weekday, so a Mon–Fri 7:00 AM Asia/Calcutta schedule — Sunday evening
 *   in New York — was drawn Tuesday to Saturday.
 *
 * The next run itself is drawn exactly as the server sent it: the cron fires at
 * `next_run_at`, whatever the rule would re-derive, and only the ones after it
 * come from the rule. A weekly `interval` is projected too, counting whole weeks
 * (Sunday-start, in the schedule's zone) from the next run's week — equivalent to
 * the engine's anchor count because the next run is itself one of its firings.
 *
 * A zone the runtime does not know is not guessed at: the schedule contributes
 * only its known next run, like a monthly one. (cb-api rejects an unknown zone on
 * write, CC-50232 F4, so this should never be reached.)
 */
export function firingsInWindow(schedule, from, to) {
  if (!schedule?.enabled || !schedule.nextRunAt) return []
  const rule = schedule.recurrence || {}
  const next = schedule.nextRunAt
  const onlyNext = () => (next >= from && next < to ? [next] : [])
  if (rule.freq !== 'daily' && rule.freq !== 'weekly') return onlyNext()
  // The loop below ends on an instant comparison, and every comparison with NaN
  // is false. D1 stores an INTEGER; anything else is not a run this can project.
  if (!(Number.isFinite(next) && Number.isFinite(from) && Number.isFinite(to))) return []

  const tz = scheduleZone(schedule)
  let first
  try { first = zonedParts(next, tz) } catch { return onlyNext() }
  // The engine's own normalisation for a present value; a rule with no time at
  // all (never from the server — `recurrence_json` is stored normalised) keeps the
  // next run's own wall time rather than inventing midnight.
  const hour = rule.hour == null ? first.hour : Math.min(23, Math.max(0, Number(rule.hour) || 0))
  const minute = rule.minute == null ? first.minute : Math.min(59, Math.max(0, Number(rule.minute) || 0))
  const interval = Math.max(1, Math.min(52, Number(rule.interval) || 1))
  const weekly = rule.freq === 'weekly'
  const days = weekly && Array.isArray(rule.byDay) && rule.byDay.length ? rule.byDay.map(Number) : [first.weekday]
  const firstSunday = addDaysToParts(first, -first.weekday)
  const step = weekly ? 1 : interval

  const out = onlyNext()
  // Start a couple of steps short of the window rather than walking from a
  // long-overdue next run; candidates before `from` are dropped below anyway.
  const skip = Math.max(1, Math.floor((from - next) / (step * DAY_MS)) - 2)
  // Bounded: a 7-day window ends it within a few steps, and a byDay naming no
  // real weekday must not spin.
  for (let k = skip; k < skip + 400; k += 1) {
    const day = addDaysToParts(first, k * step)
    if (weekly) {
      if (!days.includes(day.weekday)) continue
      const weeks = Math.round(daysBetween(firstSunday, addDaysToParts(day, -day.weekday)) / 7)
      if (weeks % interval !== 0) continue
    }
    const t = zonedTimeToUtc({ year: day.year, month: day.month, day: day.day, hour, minute }, tz)
    if (t >= to) break
    if (t >= from) out.push(t)
  }
  return out
}
